import { useState } from 'react'
import { FormControl } from 'react-bootstrap'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { NumberFormatValues, NumericFormat } from 'react-number-format'
import { ErrorLabel } from '../ErrorLabel'
import * as S from './styles'

export interface NumericInputProps<T extends FieldValues> {
  control: Control<T, any>
  fieldName: Path<T>
  label?: string
  decimalScale?: number
  allowNegative?: boolean
  disabled?: boolean
  optional?: boolean
  noMargin?: boolean
  getInputClassName?: (isFocused: boolean, hasValue: boolean) => string
}

export function NumericInput<T extends FieldValues>({
  fieldName,
  label,
  control,
  decimalScale = 0,
  allowNegative = true,
  disabled,
  optional,
  noMargin,
  getInputClassName,
}: NumericInputProps<T>) {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name: fieldName, control })

  const [localValue, setLocalValue] = useState<number | undefined>(value)
  const [isFocused, setIsFocused] = useState(false)

  const _onChange = (values: NumberFormatValues) => {
    if (values.value === '') {
      setLocalValue(undefined)
      onChange(undefined)
    } else {
      const numericValue = values.floatValue
      setLocalValue(numericValue)
      onChange(numericValue)
    }
  }

  const hasValue = localValue !== undefined && localValue !== null

  return (
    <S.Wrapper noMargin={noMargin}>
      <label
        className="text-base font-medium text-gray-800 select-none mb-1"
        htmlFor={fieldName}
      >
        {label}
        {optional && (
          <span className="italic text-sm font-normal"> - optional</span>
        )}
      </label>
      <NumericFormat
        disabled={disabled}
        allowNegative={allowNegative}
        onValueChange={_onChange}
        thousandSeparator={true}
        allowedDecimalSeparators={decimalScale ? ['.'] : []}
        decimalScale={decimalScale}
        value={localValue !== undefined ? localValue : ''}
        customInput={CustomInput}
        className={`${error?.message ? 'input-error' : ''}`}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        isFocused={isFocused}
        hasValue={hasValue}
        getInputClassName={getInputClassName}
      />
      {error?.message && <ErrorLabel message={error?.message} />}
    </S.Wrapper>
  )
}

const CustomInput = (props: any) => {
  const { isFocused, hasValue, getInputClassName, ...restProps } = props

  const customClassName = getInputClassName
    ? getInputClassName(isFocused, hasValue)
    : ''

  return (
    <FormControl
      {...restProps}
      className={`${restProps.className} ${customClassName}`}
    />
  )
}
